import { Box } from "@mui/material";
import { useId, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  SITE_HEADER_HEIGHT_PX,
  SITE_WRAP,
} from "src/domains/shared/constants/styles/site";
import {
  BRIDGE_DURATIONS,
  BridgeSceneContent,
} from "../scenes/BridgeSceneContent";
import { SceneSection, type SceneStepText } from "../scenes/SceneSection";
import {
  TERRAIN_DURATIONS,
  TerrainSceneContent,
} from "../scenes/TerrainSceneContent";
import { ClosingContact } from "../siteKit/ClosingContact";
import { EducationSection } from "../siteKit/EducationSection";
import { FeatureRow, FeatureRows } from "../siteKit/FeatureRows";
import { SiteImage } from "../siteKit/media/SiteImage";
import { PartnersStrip } from "../siteKit/PartnersStrip";
import { SectionHead } from "../siteKit/SectionHead";
import { ServiceFlow } from "../siteKit/ServiceFlow";
import { SITE_SECTION_ATTR, SiteSection } from "../siteKit/SiteSection";
import { SectionRail } from "./SectionRail";
import { StoryHero } from "./StoryHero";

const ROW_IMAGE_SIZES = "(max-width: 900px) 100vw, 640px";
/** 그리기 전 자리 높이(넓은 화면, 900px 이하) — 시안 `--cis`·`--cism` */
const SERVICE_OFFSCREEN_SIZE = [815, 946] as const;

/**
 * 홈은 장면 사이 여유를 더 둔다(구역 간격·머리말 아래·블록 간격 한 단계 크게).
 * 휴대폰은 구역 사이를 200px(화면 높이의 약 1/4)로 — 128px 이면 구역이 너무 빨리 바뀌어 보인다(2026-11-02 사용자).
 */
const LANDING_STYLES = {
  root: {
    "--sp-sec": "clamp(200px,21vw,330px)",
    "--sp-head": "clamp(52px,7vw,108px)",
    "--sp-block": "clamp(96px,9.6vw,148px)",
    [`& [${SITE_SECTION_ATTR}]`]: {
      scrollMarginTop: `${SITE_HEADER_HEIGHT_PX}px`,
    },
  },
  // 마지막 문의 구역과 푸터 사이는 조금 좁게
  closing: { paddingBottom: "calc(var(--sp-sec) * .91)" },
} as const;

/** 홈(`/`, marketing-landing) — 시안 landing.html 그대로. */
export function LandingPage() {
  const { t } = useTranslation("landing");
  const rootRef = useRef<HTMLDivElement | null>(null);
  const serviceTitleId = useId();
  const sceneSteps = (key: "view" | "bridge" | "layers") =>
    t(`site.scenes.${key}.steps`, {
      returnObjects: true,
    }) as readonly SceneStepText[];
  return (
    <Box ref={rootRef} sx={LANDING_STYLES.root}>
      <SectionRail containerRef={rootRef} />
      <StoryHero />
      <PartnersStrip inHero />
      <SceneSection
        id="platform"
        landing
        eyebrow={t("site.scenes.view.eyebrow")}
        title={t("site.scenes.view.title")}
        lead={t("site.scenes.view.lead")}
        stepsLabel={t("site.scenes.view.stepsLabel")}
        steps={sceneSteps("view")}
        durations={TERRAIN_DURATIONS.view}
        viewLabel={t("site.scenes.view.viewLabel")}
      >
        {(context) => <TerrainSceneContent context={context} preset="view" />}
      </SceneSection>
      <SceneSection
        id="survey-tools"
        landing
        eyebrow={t("site.scenes.bridge.eyebrowLanding")}
        title={t("site.scenes.bridge.title")}
        lead={t("site.scenes.bridge.lead")}
        stepsLabel={t("site.scenes.bridge.stepsLabel")}
        steps={sceneSteps("bridge")}
        durations={BRIDGE_DURATIONS}
        viewLabel={t("site.scenes.bridge.viewLabel")}
        reverse
        bridge
        after={
          <FeatureRows>
            <FeatureRow
              kicker={t("site.rows.earthwork.kicker")}
              title={t("site.rows.earthwork.title")}
              text={t("site.rows.earthwork.text")}
              media={
                <SiteImage
                  name="ew_summary"
                  sizes={ROW_IMAGE_SIZES}
                  alt={t("site.rows.earthwork.alt")}
                  placeholder="none"
                />
              }
            />
            <FeatureRow
              kicker={t("site.rows.measure.kicker")}
              title={t("site.rows.measure.title")}
              text={t("site.rows.measure.text")}
              media={
                <SiteImage
                  name="measure_zoom"
                  sizes={ROW_IMAGE_SIZES}
                  alt={t("site.rows.measure.alt")}
                  placeholder="none"
                />
              }
            />
          </FeatureRows>
        }
      >
        {(context) => <BridgeSceneContent context={context} />}
      </SceneSection>
      <SceneSection
        id="layers"
        landing
        eyebrow={t("site.scenes.layers.eyebrow")}
        title={t("site.scenes.layers.title")}
        lead={t("site.scenes.layers.lead")}
        stepsLabel={t("site.scenes.layers.stepsLabel")}
        steps={sceneSteps("layers")}
        durations={TERRAIN_DURATIONS.layers}
        viewLabel={t("site.scenes.layers.viewLabel")}
      >
        {(context) => <TerrainSceneContent context={context} preset="layers" />}
      </SceneSection>
      <SiteSection
        id="drone-service"
        labelledBy={serviceTitleId}
        skipOffscreen={SERVICE_OFFSCREEN_SIZE}
      >
        <Box sx={SITE_WRAP}>
          <SectionHead
            titleId={serviceTitleId}
            landing
            eyebrow={t("site.landing.service.eyebrow")}
            title={t("site.landing.service.title")}
            lead={t("site.landing.service.lead")}
          />
          <ServiceFlow />
        </Box>
      </SiteSection>
      <EducationSection landing />
      <ClosingContact
        id="contact"
        eyebrow={t("site.landing.closing.eyebrow")}
        title={t("site.landing.closing.title")}
        sectionSx={LANDING_STYLES.closing}
      />
    </Box>
  );
}
